import type { EstateItem, CreateEstateItemRequest } from "@/shared/types/estateTypes";

import {
  buildItemName,
  parseItemNameToForm,
  validatePropertyFields,
  propertyToItemFields,
  type PropertyFormValues,
} from "@/pages/postloss/modules/property-valuables/components/PropertyForm";
import {
  buildVehicleName,
  parseVehicleToForm,
  validateVehicleFields,
  vehicleToItemFields,
  type VehicleFormValues,
} from "@/pages/postloss/modules/property-valuables/components/VehicleForm";
import {
  parsePhysicalAssetToForm,
  validatePhysicalAssetFields,
  physicalAssetToItemFields,
  type PhysicalAssetFormValues,
} from "@/pages/postloss/modules/property-valuables/components/PhysicalAssetForm";
import {
  parseItemToForm as parseLifeInsurance,
  validateLifeInsuranceFields,
  buildMetadata as buildLifeInsuranceMetadata,
  type LifeInsuranceFormValues,
} from "@/pages/postloss/modules/benefits-eligibility/components/LifeInsuranceForm";
import {
  parseItemToForm as parseLtcInsurance,
  validateLtcInsuranceFields,
  buildMetadata as buildLtcInsuranceMetadata,
  type LtcInsuranceFormValues,
} from "@/pages/postloss/modules/benefits-eligibility/components/LtcInsuranceForm";
import {
  validateMortgageFields,
  mortgageToItemFields,
  mortgageToFormValues,
  type MortgageFormValues,
} from "@/pages/postloss/modules/debts-loans/components/MortgageForm";
import {
  parseItemToForm as parseFuneralCost,
  validateFuneralCostFields,
  funeralCostToItemFields,
  type FuneralCostFormValues,
} from "@/pages/postloss/modules/expenses/components/FuneralCostForm";
import {
  parseItemToForm as parseFinancialAccount,
  validateFinancialAccountFields,
  financialAccountToItemFields,
  type FinancialAccountFormValues,
} from "@/pages/postloss/modules/financial-accounts/components/FinancialAccountForm";
import {
  parseSafeDepositToForm,
  validateSafeDepositFields,
  safeDepositToItemFields,
  type SafeDepositFormValues,
} from "@/pages/postloss/modules/property-valuables/components/SafeDepositForm";

export type EditingState =
  | { category: "Properties"; form: PropertyFormValues }
  | { category: "Vehicles"; form: VehicleFormValues }
  | { category: "Valuables"; form: PhysicalAssetFormValues }
  | { category: "Life insurance"; form: LifeInsuranceFormValues }
  | { category: "Long-term care insurance"; form: LtcInsuranceFormValues }
  | { category: "Secured debts"; form: MortgageFormValues }
  | { category: "Ongoing Estate Expenses"; form: FuneralCostFormValues }
  | { category: "Bank accounts"; form: FinancialAccountFormValues }
  | { category: "Brokerage accounts"; form: FinancialAccountFormValues }
  | { category: "Tax-advantaged savings"; form: FinancialAccountFormValues }
  | { category: "Retirement accounts"; form: FinancialAccountFormValues }
  | { category: "Crypto accounts"; form: FinancialAccountFormValues }
  | { category: "NFTs"; form: FinancialAccountFormValues }
  | { category: "Other digital assets"; form: FinancialAccountFormValues }
  | { category: "Safe or safety deposit box"; form: SafeDepositFormValues };

export type SupportedCategory = EditingState["category"];

interface CategoryEntry {
  editTitle: string;
  deleteTitle: string;
  parse: (item: EstateItem) => unknown;
  validateFields: (form: unknown) => Partial<Record<string, string>>;
  getFile: (form: unknown) => { file: File; docType: string } | null;
  buildPayload: (form: unknown) => Partial<CreateEstateItemRequest>;
}

function category<F>(desc: {
  editTitle: string;
  deleteTitle: string;
  parse: (item: EstateItem) => F;
  validateFields: (form: F) => object;
  getFile: (form: F) => { file: File; docType: string } | null;
  buildPayload: (form: F) => Partial<CreateEstateItemRequest>;
}): CategoryEntry {
  return desc as unknown as CategoryEntry;
}

function financialAccountEntry(editTitle: string, deleteTitle: string): CategoryEntry {
  return category<FinancialAccountFormValues>({
    editTitle,
    deleteTitle,
    parse: (item) => parseFinancialAccount(item),
    validateFields: validateFinancialAccountFields,
    getFile: () => null,
    buildPayload: (form) => financialAccountToItemFields(form),
  });
}

const CATEGORY_REGISTRY: Record<SupportedCategory, CategoryEntry> = {
  Properties: category<PropertyFormValues>({
    editTitle: "Edit property",
    deleteTitle: "Delete Property",
    parse: (item) =>
      parseItemNameToForm(
        item.item_name,
        item.item_type ?? "",
        item.balance,
        item.metadata,
        item.ownership,
      ),
    validateFields: validatePropertyFields,
    getFile: (form) => (form.deedFile ? { file: form.deedFile, docType: "property_deed" } : null),
    buildPayload: (form) => {
      const fields = propertyToItemFields(form);
      return {
        item_name: buildItemName(form),
        item_type: fields.item_type,
        balance: fields.balance,
        ownership: fields.ownership,
        metadata: fields.metadata,
      };
    },
  }),
  Vehicles: category<VehicleFormValues>({
    editTitle: "Edit vehicle",
    deleteTitle: "Delete Vehicle",
    parse: (item) =>
      parseVehicleToForm(
        item.item_name,
        item.item_type ?? "",
        item.balance,
        item.notes,
        item.metadata,
      ),
    validateFields: validateVehicleFields,
    getFile: (form) => (form.titleFile ? { file: form.titleFile, docType: "vehicle_title" } : null),
    buildPayload: (form) => {
      const fields = vehicleToItemFields(form);
      return {
        item_name: buildVehicleName(form),
        item_type: fields.item_type,
        balance: fields.balance,
        metadata: fields.metadata,
      };
    },
  }),
  Valuables: category<PhysicalAssetFormValues>({
    editTitle: "Edit personal property",
    deleteTitle: "Delete Personal Property",
    parse: (item) => ({
      ...parsePhysicalAssetToForm(item.item_type ?? "", item.balance, item.notes),
      description: item.item_name || "",
    }),
    validateFields: validatePhysicalAssetFields,
    getFile: (form) => (form.file ? { file: form.file, docType: "other" } : null),
    buildPayload: (form) => {
      const fields = physicalAssetToItemFields(form);
      return {
        item_name: form.description.trim(),
        item_type: fields.item_type,
        balance: fields.balance,
        notes: fields.notes,
      };
    },
  }),
  "Life insurance": category<LifeInsuranceFormValues>({
    editTitle: "Edit policy",
    deleteTitle: "Delete Policy",
    parse: (item) =>
      parseLifeInsurance(
        item.item_name,
        item.item_type ?? "",
        item.balance,
        item.notes,
        item.metadata,
      ),
    validateFields: validateLifeInsuranceFields,
    getFile: (form) =>
      form.documentFile ? { file: form.documentFile, docType: "life_insurance_policy" } : null,
    buildPayload: (form) => ({
      item_name: form.companyName.trim(),
      item_type: form.policyType || undefined,
      balance: parseFloat(form.coverageAmount) || 0,
      metadata: buildLifeInsuranceMetadata(form),
    }),
  }),
  "Long-term care insurance": category<LtcInsuranceFormValues>({
    editTitle: "Edit LTC policy",
    deleteTitle: "Delete LTC Policy",
    parse: (item) => parseLtcInsurance(item.item_name, item.balance, item.metadata),
    validateFields: validateLtcInsuranceFields,
    getFile: (form) =>
      form.documentFile ? { file: form.documentFile, docType: "ltc_insurance_policy" } : null,
    buildPayload: (form) => ({
      item_name: form.companyName.trim(),
      item_type: "Long-term care insurance",
      balance: parseFloat(form.dailyBenefitAmount) || 0,
      metadata: buildLtcInsuranceMetadata(form),
    }),
  }),
  "Secured debts": category<MortgageFormValues>({
    editTitle: "Edit mortgage",
    deleteTitle: "Delete Mortgage",
    parse: (item) => mortgageToFormValues(item),
    validateFields: validateMortgageFields,
    getFile: () => null,
    buildPayload: (form) => {
      const fields = mortgageToItemFields(form);
      return { item_name: fields.item_name, balance: fields.balance, metadata: fields.metadata };
    },
  }),
  "Ongoing Estate Expenses": category<FuneralCostFormValues>({
    editTitle: "Edit funeral cost",
    deleteTitle: "Delete Funeral Cost",
    parse: (item) => parseFuneralCost(item.item_name, item.balance),
    validateFields: validateFuneralCostFields,
    getFile: (form) =>
      form.invoiceFile ? { file: form.invoiceFile, docType: "funeral_invoice" } : null,
    buildPayload: (form) => {
      const fields = funeralCostToItemFields(form);
      return {
        item_name: form.funeralHomeName.trim(),
        balance: fields.balance,
      };
    },
  }),
  "Safe or safety deposit box": category<SafeDepositFormValues>({
    editTitle: "Edit safe deposit box",
    deleteTitle: "Delete Safe Deposit Box",
    parse: (item) => parseSafeDepositToForm(item),
    validateFields: (form) => validateSafeDepositFields(form),
    getFile: () => null,
    buildPayload: (form) => safeDepositToItemFields(form),
  }),
  "Bank accounts": financialAccountEntry("Edit bank account", "Delete Bank Account"),
  "Brokerage accounts": financialAccountEntry("Edit brokerage account", "Delete Brokerage Account"),
  "Tax-advantaged savings": financialAccountEntry(
    "Edit tax-advantaged account",
    "Delete Tax-Advantaged Account",
  ),
  "Retirement accounts": financialAccountEntry(
    "Edit retirement account",
    "Delete Retirement Account",
  ),
  "Crypto accounts": financialAccountEntry("Edit crypto account", "Delete Crypto Account"),
  NFTs: financialAccountEntry("Edit NFT", "Delete NFT"),
  "Other digital assets": financialAccountEntry("Edit digital asset", "Delete Digital Asset"),
};

export const SUPPORTED_CATEGORIES = new Set<string>(Object.keys(CATEGORY_REGISTRY));

export function isSupportedCategory(category: string): category is SupportedCategory {
  return SUPPORTED_CATEGORIES.has(category);
}

export function parseItem(item: EstateItem): EditingState | null {
  if (!isSupportedCategory(item.category)) return null;
  const desc = CATEGORY_REGISTRY[item.category];
  return { category: item.category, form: desc.parse(item) } as EditingState;
}

export function getDialogTitle(category: SupportedCategory, itemType?: string): string {
  if (category === "Secured debts" && itemType === "Home equity loans") {
    return "Edit home equity loan";
  }
  return CATEGORY_REGISTRY[category].editTitle;
}

export function getDeleteTitle(category: SupportedCategory, itemType?: string): string {
  if (category === "Secured debts" && itemType === "Home equity loans") {
    return "Delete Home Equity Loan";
  }
  return CATEGORY_REGISTRY[category].deleteTitle;
}

export function getFileFromForm(state: EditingState): { file: File; docType: string } | null {
  return CATEGORY_REGISTRY[state.category].getFile(state.form);
}

export function validateFormFields(state: EditingState): Partial<Record<string, string>> {
  return CATEGORY_REGISTRY[state.category].validateFields(state.form);
}

export function buildUpdatePayload(state: EditingState): Partial<CreateEstateItemRequest> {
  return CATEGORY_REGISTRY[state.category].buildPayload(state.form);
}
